import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dmClient } from '../../services/dmClient';
import usePageTitle from '../../hooks/usePageTitle';
import './caseBuilder.css';

const REFERENCE_TYPES = [
  'PAYER_POLICY',
  'LCD_NCD',
  'CONTRACT_FEE_SCHEDULE',
  'ADJUSTMENT_REMARK_CODES',
  'PROCEDURE_CODING',
  'DIAGNOSIS_CODING',
  'NCCI_PTP',
  'MUE',
];

const CLAIM_KEYS = [
  'insurance.plan.plan_id',
  'insurance.payer_id',
  'claim.date_of_service_start',
  'claim.claim_type',
  'claim.lines[].procedure.code',
  'claim.lines[].procedure.modifiers',
  'claim.lines[].adjustments[].carc',
  'claim.lines[].adjustments[].rarc_codes',
  'claim.diagnoses[].code',
  'claim.lines[].units',
  'claim.service_facility.place_of_service',
];

const KINDS = [
  { id: 'graph', label: 'Graph' },
  { id: 'code', label: 'Exact code lookup' },
  { id: 'code_range', label: 'Code range' },
  { id: 'lookup_where', label: 'Lookup filter' },
];

const EMPTY = {
  instance_map_id: '',
  enabled: true,
  sort_order: 100,
  match_path: 'insurance.plan.plan_id',
  match_value: '',
  match_value_from: '',
  match_value_to: '',
  reference_type: 'PAYER_POLICY',
  target_kind: 'graph',
  graph_id: '',
  reference_id: '',
  lookup_category: '',
  codesText: '',
  code_from: '',
  code_to: '',
  conditions: [{ field: '', op: 'eq', value: '' }],
  version: '',
  effective_from: '',
  effective_to: '',
  applicability_status: '',
};

function lines(value) {
  return String(value || '').split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean);
}

function conditionValue(cond) {
  if (cond.op === 'in' || cond.op === 'between') return lines(cond.value);
  return cond.value;
}

function fromRow(row) {
  return {
    ...EMPTY,
    ...row,
    codesText: (row.codes || []).join('\n'),
    conditions: (row.lookup_where || []).length
      ? row.lookup_where.map((cond) => ({
        field: cond.field || '',
        op: cond.op || 'eq',
        value: Array.isArray(cond.value) ? cond.value.join(', ') : (cond.value || ''),
      }))
      : [{ field: '', op: 'eq', value: '' }],
  };
}

function targetLabel(row) {
  if (row.target_kind === 'graph') return row.graph_id || '—';
  if (row.target_kind === 'code') return `${row.lookup_category}: ${(row.codes || []).join(', ')}`;
  if (row.target_kind === 'code_range') return `${row.lookup_category || 'range'} ${row.code_from}–${row.code_to}`;
  const parts = (row.lookup_where || []).map((cond) => {
    const value = Array.isArray(cond.value) ? cond.value.join(', ') : cond.value;
    return `${cond.field} ${cond.op} ${value}`;
  });
  return `${row.lookup_category}: ${parts.join('; ')}`;
}

function valueLabel(row) {
  if (row.target_kind === 'code_range') return `${row.match_value_from}–${row.match_value_to}`;
  return row.match_value || '—';
}

export default function ReferenceInstancesPage() {
  usePageTitle('Instance mapping');
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dmClient.get('/case-builder/reference-instances')
      .then((res) => {
        if (!cancelled) setItems(res.data?.data?.items || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Could not load instance mappings.');
      });
    return () => { cancelled = true; };
  }, []);

  function payload() {
    const kind = draft.target_kind;
    const conditions = (draft.conditions || [])
      .filter((cond) => cond.field && cond.value !== '')
      .map((cond) => ({ field: cond.field.trim(), op: cond.op, value: conditionValue(cond) }));
    if (kind === 'graph' && !draft.graph_id.trim()) return 'A graph mapping needs a graph id.';
    if (kind === 'code' && (!draft.lookup_category.trim() || lines(draft.codesText).length === 0)) {
      return 'A code lookup needs a category and at least one code.';
    }
    if (kind === 'code_range' && (!draft.match_value_from.trim() || !draft.match_value_to.trim() || !draft.code_from.trim() || !draft.code_to.trim())) {
      return 'A code range needs the claim band and the lookup band.';
    }
    if (kind === 'lookup_where' && (!draft.lookup_category.trim() || conditions.length === 0)) {
      return 'A lookup filter needs a category and at least one condition.';
    }
    if (kind !== 'code_range' && !draft.match_value.trim()) return 'Enter the claim value that selects this row.';
    return {
      instance_map_id: draft.instance_map_id || undefined,
      enabled: draft.enabled,
      sort_order: Number(draft.sort_order) || 100,
      match_path: draft.match_path,
      match_value: kind === 'code_range' ? '' : draft.match_value.trim(),
      match_value_from: kind === 'code_range' ? draft.match_value_from.trim() : '',
      match_value_to: kind === 'code_range' ? draft.match_value_to.trim() : '',
      reference_type: draft.reference_type,
      target_kind: kind,
      graph_id: kind === 'graph' ? draft.graph_id.trim() : '',
      reference_id: draft.reference_id.trim(),
      lookup_category: kind === 'graph' ? '' : draft.lookup_category.trim(),
      codes: kind === 'code' ? lines(draft.codesText) : [],
      code_from: kind === 'code_range' ? draft.code_from.trim() : '',
      code_to: kind === 'code_range' ? draft.code_to.trim() : '',
      lookup_where: kind === 'lookup_where' ? conditions : [],
      version: draft.version.trim(),
      effective_from: draft.effective_from.trim(),
      effective_to: draft.effective_to.trim(),
      applicability_status: draft.applicability_status.trim(),
    };
  }

  async function save(event) {
    event.preventDefault();
    const body = payload();
    if (typeof body === 'string') {
      setError(body);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await dmClient.put('/case-builder/reference-instances', body);
      setItems(res.data?.data?.items || []);
      setDraft(null);
      setMessage('Instance mapping saved for this company.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this instance mapping.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(instanceId) {
    setBusy(true);
    setError('');
    try {
      const res = await dmClient.delete(`/case-builder/reference-instances/${instanceId}`);
      setItems(res.data?.data?.items || []);
      if (draft?.instance_map_id === instanceId) setDraft(null);
      setMessage('Instance mapping removed.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove this instance mapping.');
    } finally {
      setBusy(false);
    }
  }

  function setCondition(index, patch) {
    const conditions = draft.conditions.map((cond, i) => (i === index ? { ...cond, ...patch } : cond));
    setDraft({ ...draft, conditions });
  }

  return (
    <div className="cb-page">
      <header className="cb-head">
        <div>
          <h1>Instance mapping</h1>
          <p>
            <Link to="/case-builder">Case Builder</Link>
            {' · a claim value selects a graph, a code, a code range, or a lookup filter. Each plan is its own row.'}
          </p>
        </div>
        <button type="button" className="dm-btn dm-btn--primary" onClick={() => { setDraft({ ...EMPTY, conditions: [{ field: '', op: 'eq', value: '' }] }); setMessage(''); }}>
          Add mapping
        </button>
      </header>

      {error ? <p className="cb-error">{error}</p> : null}
      {message ? <p className="cb-note">{message}</p> : null}

      <div className="cb-table-wrap">
        <table className="cb-table">
          <thead>
            <tr>
              <th>Claim path</th>
              <th>Value</th>
              <th>Reference</th>
              <th>Target</th>
              <th>Points at</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6}>No instance mappings for this company.</td></tr>
            ) : items.map((item) => (
              <tr key={item.instance_map_id}>
                <td>
                  <div className="cb-strong">{item.match_path}</div>
                  <div className="cb-sub">{item.enabled ? 'Enabled' : 'Disabled'}</div>
                </td>
                <td>{valueLabel(item)}</td>
                <td>{item.reference_type}</td>
                <td>{KINDS.find((kind) => kind.id === item.target_kind)?.label || item.target_kind}</td>
                <td>{targetLabel(item)}</td>
                <td>
                  <div className="cb-actions">
                    <button type="button" onClick={() => { setDraft(fromRow(item)); setMessage(''); }}>Edit</button>
                    <button type="button" onClick={() => remove(item.instance_map_id)} disabled={busy}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {draft ? (
        <form className="cb-panel" onSubmit={save}>
          <h2>{draft.instance_map_id ? 'Edit instance' : 'New instance'}</h2>
          <div className="cb-grid">
            <label className="cb-field">
              Claim path
              <select value={draft.match_path} onChange={(e) => setDraft({ ...draft, match_path: e.target.value })}>
                {CLAIM_KEYS.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label className="cb-field">
              Reference type
              <select value={draft.reference_type} onChange={(e) => setDraft({ ...draft, reference_type: e.target.value })}>
                {REFERENCE_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label className="cb-field">
              Target kind
              <select value={draft.target_kind} onChange={(e) => setDraft({ ...draft, target_kind: e.target.value })}>
                {KINDS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </select>
            </label>
            <label className="cb-field">
              Sort
              <input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
            </label>
            <label className="cb-check">
              <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
              Enabled
            </label>
          </div>

          {draft.target_kind === 'code_range' ? (
            <div className="cb-grid">
              <label className="cb-field">
                Claim value from
                <input value={draft.match_value_from} onChange={(e) => setDraft({ ...draft, match_value_from: e.target.value })} required />
              </label>
              <label className="cb-field">
                Claim value to
                <input value={draft.match_value_to} onChange={(e) => setDraft({ ...draft, match_value_to: e.target.value })} required />
              </label>
            </div>
          ) : (
            <label className="cb-field">
              Claim value
              <input value={draft.match_value} onChange={(e) => setDraft({ ...draft, match_value: e.target.value })} placeholder="CIGNA-PPO-GOLD" required />
            </label>
          )}

          {draft.target_kind === 'graph' ? (
            <div className="cb-grid">
              <label className="cb-field">
                Graph id
                <input value={draft.graph_id} onChange={(e) => setDraft({ ...draft, graph_id: e.target.value })} required />
              </label>
              <label className="cb-field">
                Reference id
                <input value={draft.reference_id} onChange={(e) => setDraft({ ...draft, reference_id: e.target.value })} />
              </label>
            </div>
          ) : null}

          {draft.target_kind === 'code' ? (
            <div className="cb-grid">
              <label className="cb-field">
                Lookup category
                <input value={draft.lookup_category} onChange={(e) => setDraft({ ...draft, lookup_category: e.target.value })} placeholder="CARC, HCPCS, ICD-10-CM" required />
              </label>
              <label className="cb-field">
                Codes
                <textarea rows={4} value={draft.codesText} onChange={(e) => setDraft({ ...draft, codesText: e.target.value })} placeholder="One code per line" required />
              </label>
            </div>
          ) : null}

          {draft.target_kind === 'code_range' ? (
            <div className="cb-grid">
              <label className="cb-field">
                Lookup category
                <input value={draft.lookup_category} onChange={(e) => setDraft({ ...draft, lookup_category: e.target.value })} placeholder="HCPCS or ICD-10-CM" />
              </label>
              <label className="cb-field">
                Lookup code from
                <input value={draft.code_from} onChange={(e) => setDraft({ ...draft, code_from: e.target.value })} required />
              </label>
              <label className="cb-field">
                Lookup code to
                <input value={draft.code_to} onChange={(e) => setDraft({ ...draft, code_to: e.target.value })} required />
              </label>
            </div>
          ) : null}

          {draft.target_kind === 'lookup_where' ? (
            <>
              <label className="cb-field">
                Lookup category
                <input value={draft.lookup_category} onChange={(e) => setDraft({ ...draft, lookup_category: e.target.value })} placeholder="MUE, NCCI_PTP, RVU_PFS" required />
              </label>
              {draft.conditions.map((cond, index) => (
                <div className="cb-grid" key={index}>
                  <label className="cb-field">
                    Field
                    <input value={cond.field} onChange={(e) => setCondition(index, { field: e.target.value })} placeholder="suffix" />
                  </label>
                  <label className="cb-field">
                    Operator
                    <select value={cond.op} onChange={(e) => setCondition(index, { op: e.target.value })}>
                      <option value="eq">equals</option>
                      <option value="in">in</option>
                      <option value="between">between</option>
                      <option value="like">like</option>
                    </select>
                  </label>
                  <label className="cb-field">
                    Value
                    <input value={cond.value} onChange={(e) => setCondition(index, { value: e.target.value })} placeholder="PRAC" />
                  </label>
                </div>
              ))}
              <button
                type="button"
                className="dm-btn dm-btn--secondary"
                onClick={() => setDraft({ ...draft, conditions: [...draft.conditions, { field: '', op: 'eq', value: '' }] })}
              >
                Add condition
              </button>
            </>
          ) : null}

          <div className="cb-grid">
            <label className="cb-field">
              Version
              <input value={draft.version} onChange={(e) => setDraft({ ...draft, version: e.target.value })} />
            </label>
            <label className="cb-field">
              Effective from
              <input value={draft.effective_from} onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })} />
            </label>
            <label className="cb-field">
              Effective to
              <input value={draft.effective_to} onChange={(e) => setDraft({ ...draft, effective_to: e.target.value })} />
            </label>
            <label className="cb-field">
              Applicability
              <input value={draft.applicability_status} onChange={(e) => setDraft({ ...draft, applicability_status: e.target.value })} />
            </label>
          </div>

          <div className="cb-actions">
            <button type="submit" className="dm-btn dm-btn--primary" disabled={busy}>Save</button>
            <button type="button" className="dm-btn dm-btn--secondary" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
